import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  SelectProps,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { castArray, compact, find, first, get, isEmpty, join, map, orderBy, split, uniq, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import LazyLoadSelect from 'components/atoms/Dropdown/LazyLoadDropDown';
import { Job, JobStatus, JobType } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { humanize } from 'utils/helpers';
import { getJobQuery } from 'utils/queryHooks/jobs/useJob';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';

export interface JobMultiSelectProps
  extends Omit<SelectProps, 'onScroll' | 'label' | 'value' | 'onChange' | 'multiple'> {
  debug?: boolean;
  label: string;
  jobType: JobType | JobType[];
  jobIds?: string[];
  jobName?: string;
  orchestrationIds?: string[];
  orchestrationIdTextFilter?: string;
  users?: Record<string, string>[];
  senders?: string[];
  slideIds?: string[];
  statuses?: JobStatus[];
  selectedJobIds: string[];
  setSelectedJobIds: (jobIds: string[]) => void;
  onLoadFullJobs?: (jobs: Job[]) => void;
  autoAssignLastJob?: boolean;
  errors?: any;
  field?: string;
  helperText?: string;
  studyId?: string;
  withChildren?: boolean;
  formControlProps?: React.ComponentProps<typeof LazyLoadSelect>['formControlProps'];
  renderJobOptionDetails?: (job: Job) => React.ReactNode;
}

const PAGE_SIZE = 6;

const isString = (value: unknown): value is string => typeof value === 'string';

const JobMultiSelect: React.FC<JobMultiSelectProps> = ({
  debug = false,
  label,
  jobType,
  jobIds,
  jobName,
  orchestrationIds,
  orchestrationIdTextFilter,
  users,
  senders,
  slideIds,
  statuses = [JobStatus.Completed],
  withChildren = true,
  selectedJobIds,
  autoAssignLastJob,
  setSelectedJobIds,
  onLoadFullJobs,
  errors,
  field,
  helperText,
  studyId,
  renderJobOptionDetails,
  ...selectProps
}) => {
  const [jobsPage, setJobsPage] = useState(1);
  const [allJobs, setAllJobs] = useState<Job[]>([]);

  const normalizedSelectedJobIds = useMemo(() => uniq(selectedJobIds).filter(Boolean), [selectedJobIds]);

  const jobsFilterKey = useMemo(
    () =>
      JSON.stringify({
        debug,
        jobType: castArray(jobType),
        jobIds,
        jobName,
        orchestrationIds,
        orchestrationIdTextFilter,
        users,
        senders,
        slideIds,
        statuses,
        withChildren,
        studyId,
      }),
    [
      debug,
      jobType,
      jobIds,
      jobName,
      orchestrationIds,
      orchestrationIdTextFilter,
      users,
      senders,
      slideIds,
      statuses,
      withChildren,
      studyId,
    ]
  );

  useEffect(() => {
    setJobsPage(1);
    setAllJobs([]);
  }, [jobsFilterKey]);

  const {
    data: jobs,
    isFetching: isJobsFetching,
    isError: isJobsError,
  } = useJobs({
    debug,
    page: jobsPage,
    pageSize: PAGE_SIZE,
    additionalFilters: {
      ...(!isEmpty(jobType) && { types: castArray(jobType) }),
      jobIds,
      jobName,
      orchestrationIds,
      orchestrationIdTextFilter,
      users,
      senders,
      slideIds,
      statuses,
    },
    withChildren,
    ...(studyId ? { studyId } : {}),
  });

  const totalPages: number | null = jobs?.totalJobs ? Math.ceil(jobs.totalJobs / PAGE_SIZE) : null;

  const loadMoreJobs = () => {
    if (totalPages && jobsPage < totalPages) {
      setJobsPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (jobs) {
      setAllJobs((prevJobs) => orderBy(uniqBy([...prevJobs, ...(jobs.jobs as Job[])], 'id'), 'startedAt', 'desc'));
    }
  }, [jobs]);

  const effectiveSelectedJobIds = useMemo(
    () =>
      autoAssignLastJob && isEmpty(normalizedSelectedJobIds) && !isEmpty(allJobs)
        ? [first(allJobs)?.id || '']
        : normalizedSelectedJobIds,
    [allJobs, autoAssignLastJob, normalizedSelectedJobIds]
  );

  useEffect(() => {
    if (autoAssignLastJob && isEmpty(normalizedSelectedJobIds) && !isEmpty(effectiveSelectedJobIds[0])) {
      setSelectedJobIds(effectiveSelectedJobIds);
    }
  }, [autoAssignLastJob, effectiveSelectedJobIds, normalizedSelectedJobIds, setSelectedJobIds]);

  const selectedJobQueries = useQueries({
    queries: map(effectiveSelectedJobIds, (selectedJobId) => ({
      ...getJobQuery(selectedJobId),
      enabled: !isEmpty(selectedJobId),
      retry: true,
    })),
  });

  const selectedFullJobs = useMemo(
    () => compact(map(selectedJobQueries, (query) => query.data as Job | undefined)),
    [selectedJobQueries]
  );

  const isLoadingSelectedJobs = selectedJobQueries.some((query) => query.isLoading || query.isFetching);

  const mergedJobs = useMemo(
    () => orderBy(uniqBy([...allJobs, ...selectedFullJobs], 'id'), 'startedAt', 'desc'),
    [allJobs, selectedFullJobs]
  );

  useEffect(() => {
    if (onLoadFullJobs && !isLoadingSelectedJobs) {
      onLoadFullJobs(selectedFullJobs);
    }
  }, [isLoadingSelectedJobs, onLoadFullJobs, selectedFullJobs]);

  const jobOptions = useMemo(
    () =>
      map(
        mergedJobs.filter((job): job is Job & { id: string } => Boolean(job.id)),
        (job) => {
          const optionLabel = join(compact([job.name || job.id, formatDateTimeForUser(job.startedAt)]), ' ');
          return {
            value: job.id,
            label: optionLabel,
            menuContent: renderJobOptionDetails ? (
              <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%' }}>
                <Typography variant="body2" noWrap title={optionLabel}>
                  {optionLabel}
                </Typography>
                {renderJobOptionDetails(job)}
              </Stack>
            ) : undefined,
          };
        }
      ),
    [mergedJobs, renderJobOptionDetails]
  );

  const errorMessage = field ? get(errors, field)?.message : undefined;

  const getSelectedLabels = (selectedValues: unknown): string => {
    const selectedIds = (Array.isArray(selectedValues) ? selectedValues : castArray(selectedValues)).filter(isString);
    return join(
      map(selectedIds, (selectedId) => find(jobOptions, { value: selectedId })?.label || selectedId),
      ', '
    );
  };

  const handleChange = (event: any) => {
    const nextValue = event.target.value;
    const nextJobIds =
      typeof nextValue === 'string'
        ? split(nextValue, ',')
        : (Array.isArray(nextValue) ? nextValue : castArray(nextValue)).filter(isString);
    setSelectedJobIds(nextJobIds.filter(Boolean));
  };

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={Boolean(errorMessage)}
      className={errorMessage ? 'error' : undefined}
    >
      <LazyLoadSelect
        label={label}
        options={jobOptions}
        loadMoreOptions={loadMoreJobs}
        isLoading={isJobsFetching}
        required
        multiple
        error={Boolean(errorMessage)}
        value={effectiveSelectedJobIds}
        onChange={handleChange}
        renderValue={getSelectedLabels}
        helperText={humanize(errorMessage) || helperText || (isJobsError ? 'Error loading jobs' : undefined)}
        loadingTitle="Loading jobs..."
        endAdornment={
          isLoadingSelectedJobs ? (
            <InputAdornment position="end" sx={{ marginRight: 6 }}>
              <Tooltip title="Loading selected job details...">
                <CircularProgress size={20} />
              </Tooltip>
            </InputAdornment>
          ) : undefined
        }
        {...selectProps}
      />
      {Boolean(errorMessage) && (
        <FormHelperText>
          {`${errorMessage}`}.{' '}
          {isEmpty(effectiveSelectedJobIds)
            ? 'Please select at least one Threshold Job.'
            : 'Problem with one or more selected jobs.'}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default JobMultiSelect;
